import { useState } from 'react';
import { Loader2, Tag, X } from 'lucide-react';
import { API, publicApi } from '@/lib/api';

const LABELS = {
  fr: { ph: 'Code promo', apply: 'Appliquer', applied: 'Code appliqué', remove: 'Retirer', min: 'Panier minimum de {n} requis', row: 'Code promo', auto: 'Appliqué automatiquement' },
  en: { ph: 'Promo code', apply: 'Apply', applied: 'Code applied', remove: 'Remove', min: 'Minimum order of {n} required', row: 'Promo code', auto: 'Applied automatically' },
  es: { ph: 'Código promocional', apply: 'Aplicar', applied: 'Código aplicado', remove: 'Quitar', min: 'Pedido mínimo de {n}', row: 'Código promocional', auto: 'Aplicado automáticamente' },
  de: { ph: 'Gutscheincode', apply: 'Anwenden', applied: 'Code angewendet', remove: 'Entfernen', min: 'Mindestbestellwert {n}', row: 'Gutscheincode', auto: 'Automatisch angewendet' },
  it: { ph: 'Codice promo', apply: 'Applica', applied: 'Codice applicato', remove: 'Rimuovi', min: 'Ordine minimo di {n}', row: 'Codice promo', auto: 'Applicato automaticamente' },
  nl: { ph: 'Kortingscode', apply: 'Toepassen', applied: 'Code toegepast', remove: 'Verwijderen', min: 'Minimale bestelling {n}', row: 'Kortingscode', auto: 'Automatisch toegepast' },
  pl: { ph: 'Kod rabatowy', apply: 'Zastosuj', applied: 'Kod zastosowany', remove: 'Usuń', min: 'Minimalne zamówienie {n}', row: 'Kod rabatowy', auto: 'Zastosowano automatycznie' },
  sv: { ph: 'Rabattkod', apply: 'Använd', applied: 'Kod tillämpad', remove: 'Ta bort', min: 'Minsta order {n}', row: 'Rabattkod', auto: 'Tillämpad automatiskt' },
};

export const promoLabels = (lang) => LABELS[lang] || LABELS.fr;

// Client-side mirror of services/promo_svc.discount_for (server stays authoritative).
export const computePromoDiscount = (promo, amount) => {
  if (!promo || amount < (promo.min_amount || 0)) return 0;
  const raw = promo.type === 'percent' ? (amount * Math.min(100, promo.value)) / 100 : promo.value;
  return Math.round(Math.max(0, Math.min(raw, amount)) * 100) / 100;
};

export const PromoCodeField = ({ productId, amount, email, promo, setPromo, lang, accent, money, inputClass = '', tidSuffix = '' }) => {
  const L = promoLabels(lang);
  const tid = (n) => `${n}${tidSuffix}`;
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const apply = async () => {
    if (!code.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      const { data } = await publicApi.post(`${API}/public/checkout/promo`, { product_id: productId, code: code.trim(), amount, email: email || '' });
      setPromo(data); setCode('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Code invalide');
    } finally { setBusy(false); }
  };

  if (promo) {
    const below = amount < (promo.min_amount || 0);
    return (
      <div data-testid={tid('promo-applied')} className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
        <span className="flex items-center gap-2 text-emerald-800 min-w-0">
          <Tag className="h-4 w-4 shrink-0" />
          <span className="font-semibold tracking-wide truncate" data-testid={tid('promo-applied-code')}>{promo.code}</span>
          <span className="text-xs text-emerald-700 truncate">
            {below ? L.min.replace('{n}', money(promo.min_amount)) : (promo.type === 'percent' ? `−${promo.value} %` : `−${money(promo.value)}`)}
            {promo.auto && !below && <span data-testid={tid('promo-auto-label')}> · {L.auto}</span>}
          </span>
        </span>
        <button type="button" onClick={() => setPromo(null)} aria-label={L.remove} data-testid={tid('promo-remove-btn')}
          className="p-1 rounded text-emerald-800 hover:bg-emerald-100 transition-colors"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div data-testid={tid('promo-field')}>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setErr(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
          placeholder={L.ph} data-testid={tid('promo-code-input')} autoComplete="off"
          className={`flex-1 min-w-0 h-11 rounded-md border border-zinc-300 bg-white px-3 text-sm uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-zinc-900/10 ${inputClass}`} />
        <button type="button" onClick={apply} disabled={!code.trim() || busy} data-testid={tid('promo-apply-btn')}
          style={code.trim() ? { backgroundColor: accent || '#111111', color: '#fff', borderColor: 'transparent' } : undefined}
          className="h-11 px-5 rounded-md border border-zinc-300 text-sm font-medium text-zinc-500 transition-colors disabled:cursor-not-allowed">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : L.apply}
        </button>
      </div>
      {err && <p data-testid={tid('promo-error')} className="mt-1.5 text-xs text-red-600">{err}</p>}
    </div>
  );
};

export const PromoSummaryRow = ({ promo, discount, lang, money }) => {
  if (!promo || !(discount > 0)) return null;
  return (
    <div className="flex justify-between text-emerald-700" data-testid="checkout-promo-discount">
      <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> {promoLabels(lang).row} ({promo.code})</span>
      <span className="font-mono">−{money(discount)}</span>
    </div>
  );
};
